import React, { useRef, useState, useMemo } from "react";
import { getProjectEditMilestonesApi, saveProjectEditMilestoneApi, getProjectMilestoneStatusApi, updateProjectStatusApi } from "@/services"
import { useRequest } from "ahooks"
import { Link, generatePath, useSearchParams } from 'react-router-dom';
import { Button, Card, Select, Space, Table, DatePicker, TableProps, Tag, Tooltip, Typography, Pagination, Modal, Spin, Switch, ConfigProvider, message, Input } from 'antd';
import { IthHelmet, PpmProjectListRefreshEventName, ProjectStatus, ThEmpty, dispatchPpmProjectUpdateModalOpenEvent } from '@/components';
import styles from "../list/index.module.scss"
import Edit, { k_milestoneStatus, statusOptions } from "../detail/milestone/edit"
import { getMilestoneData } from "../detail/milestone/index"
import dayjs from 'dayjs';
import { parseTime } from "@/utils"
import "./index.scss"
import {
    QuestionCircleOutlined
} from '@ant-design/icons';
import { getMinDateForMilestone, getPrevNonSpecialNodeIndex } from "../detail/project"

const k_circleNodeParentCode = "69"

const warnStatus = ["finished", "discontinued", "on_pause"]

const Index = () => {
    const [searchParams] = useSearchParams();
    const email = searchParams.get('email') || ''
    const [hideInactive, setHideInactive] = useState(true)

    const [onlyShowPA, setOnlyShowPA] = useState(true)
    const currentDate = useRef(new Date())

    const [renderList, setRenderList] = useState<API.PROJECT.EditMilestone[]>([])
    const [page, setPage] = useState(1)
    const pageSize = useRef(30)
    const [showCompleted, setShowCompleted] = useState(true)
    const [keyword, setKeyword] = useState("")
    const {data, loading, run: fetchList } = useRequest(getProjectEditMilestonesApi, {
        defaultParams: [{ page: 1, pageSize: pageSize.current, email, showAll: !hideInactive, onlyShowPa: onlyShowPA, showCompleted: showCompleted, keyword }],
        onSuccess: (res)=>{
            setRenderList(res.list)
        }
    })
    const { data: allStatus = [] } = useRequest(getProjectMilestoneStatusApi)
    const onUpdateList = (params: Req.MilestoneListParams = {}) => {
        const newparams = {
            page: page,
            pageSize: pageSize.current,
            showCompleted: showCompleted,
            email,
            showAll: !hideInactive,
            onlyShowPa: onlyShowPA,
            keyword,
            ...params,
        }
        fetchList(newparams)
    }
    const onSearch = (value: string) => {
        const kw = value.trim()
        setKeyword(kw)
        setPage(1)
        onUpdateList({ page: 1, keyword: kw })
    }
    const onPageChange = (v: number) => {
        setPage(v)
        onUpdateList({ page: v })
    }
    const [currentEditMilestone, setCurrentEditMilestone] = useState<API.PROJECT.EditMilestone>()
    const { data: list, loading: listLoading, run: fetchItems } = useRequest(getMilestoneData, {
        manual: true
    })

    const [editModalVisible, setEditModalVisible] = useState(false)

    const onEditComplete = () => {
        onCloseEdit()
        onUpdateList()
    }
    const onCloseEdit = () => {
        setEditModalVisible(false)
        setCurrentEditMilestone(undefined)
    }
    const onEdit = (item: API.PROJECT.EditMilestone) => {
        if (!item.id) return

        setCurrentEditMilestone(item)
        setEditModalVisible(true);
        fetchItems(item.id, item.plot?.ID);
    }
    
    const [currentEditItem, setCurrentEditItem] = useState<API.PROJECT.EditMilestone & { _status: number, _projectStatusCode: string}>()
    const [showCompletedDatePop, setShowCompletedDatePop] = useState(false)
    const [showProjectStatusWarnPop, setShowProjectStatusWarnPop] = useState(false)
    const onRowStatusChange = (val: number, item: API.PROJECT.EditMilestone) => {
        if(val === item.status || item.status === 1) return
        setCurrentEditItem({
            ...item,
            _status: item.status,
            status: val,
            _projectStatusCode: item.projectStatusCode
        })
        setRenderList((vals) => {
            return vals.map(v => {
                if (v.id === item.id && item.plot?.ID === v.plot?.ID) {
                    return {
                        ...item,
                        status: val
                    }
                }
                return v
            })
        })
        if (val === 1) {
            const curDate = new Date()
            curDate.setHours(0, 0, 0)
            let completedDateStr = parseTime(new Date())
            if (item.standardDate) {
                const planDate = new Date(item.standardDate)
                planDate.setHours(0,0,0)
                if (planDate.getTime() < curDate.getTime()) {
                    completedDateStr = item.standardDate
                }
            }
            completedDate.current = completedDateStr
            setShowCompletedDatePop(true)
            fetchItems(item.id, item.plot?.ID);
        }
    }
    const onProjectStatusChange = (val: string, item: API.PROJECT.EditMilestone) => {
        
        setRenderList((vals) => {
            return vals.map(v => {
                if (v.id === item.id && item.plot?.ID === v.plot?.ID) {
                    return {
                        ...item,
                        projectStatusCode: val
                    }
                }
                return v
            })
        })
        if (warnStatus.includes(val)) {
            setCurrentEditItem({
                ...item,
                _projectStatusCode: item.projectStatusCode,
                projectStatusCode: val,
                _status: item.status
            })
            setShowProjectStatusWarnPop(true)
        } else {
            runSaveProjectStatus(item.id, val)
        }
    }
    const onCancelEditStatus = () => {
        setShowCompletedDatePop(false)
        setShowProjectStatusWarnPop(false)
        setRenderList((vals) => {
            return vals.map(v => {
                if (currentEditItem && v.id === currentEditItem.id && currentEditItem.plot?.ID === v.plot?.ID) {
                    return {
                        ...v,
                        status: currentEditItem._status,
                        projectStatusCode: currentEditItem._projectStatusCode
                    }
                }
                return v
            })
        })
        setCurrentEditItem(undefined)
    }
    const completedDate = useRef("")
    const circleCompletionDate = useRef("")
    const { loading: saveLoading, run: runSaveItem } = useRequest(saveProjectEditMilestoneApi, {
        manual: true,
        onSuccess: () => {
            onUpdateList()
            setCurrentEditItem(undefined)
        }
    })
    const onSaveEditStatus = () => {
        if (saveLoading || !completedDate.current || !currentEditItem) return
        

        let params: Request.EditMilestoneItem = {
            plotID: currentEditItem.plot?.ID,
            code: currentEditItem.nextMilestoneCode,
            milestoneName: currentEditItem.nextMilestone,
            status: currentEditItem.status,
            actualDate: completedDate.current
        }
        if (currentEditItem.nextMilestoneCode === k_circleNodeParentCode) {

            if (!circleCompletionDate.current) {
                message.error("外协交圈时间不可为空~")
                return
            }
            params.circleCompletion = {
                status: k_milestoneStatus.finished,
                actualDate: circleCompletionDate.current
            }
        }

        setShowCompletedDatePop(false)

        runSaveItem(currentEditItem.id, params)
    }
    const { loading: saveProjectStatusLoading, run: runSaveProjectStatus } = useRequest(updateProjectStatusApi, {
        manual: true,
        onSuccess: () => {
            onUpdateList()
            setCurrentEditItem(undefined)
        }
    })
    const onSaveProjectStatus = () => {
        if (saveProjectStatusLoading || !currentEditItem) return
        setShowProjectStatusWarnPop(false)
        runSaveProjectStatus(currentEditItem.id, currentEditItem.projectStatusCode, currentEditItem.plot?.ID)
    }
    const tableColumns: TableProps<API.PROJECT.EditMilestone>['columns'] = [
        {
            key: 'name',
            dataIndex: 'name',
            title: '项目名称',
            minWidth: 200,
            render: (name, record) => {
                if (!name) return null;
                return (
                    <Link
                        to={generatePath('/projects/:projectID/milestone', { projectID: record.id.toString() })}
                        className="hover:underline text-inherit"
                        target="_blank"
                    >
                        {name}
                    </Link>
                );
            },
        },
        {
            key: 'plot',
            dataIndex: 'plot',
            title: '地块名称',
            width: 160,
            align: "center",
            render: (name, record) => {
                if (!record.plot) return null;
                return record.plot.plotName
            },
        },
        {
            key: 'role',
            dataIndex: 'role',
            title: '角色',
            width: 160,
            align: "center",
        },
        {
            key: 'projectStatusCode',
            dataIndex: 'projectStatusCode',
            title: '项目状态',
            width: 160,
            align: "center",
            render: (text, record) => {
                return (
                    <Select styles={{popup: {root: {width: 150}}}} options={allStatus.map(v => ({ label: v.Name, value: v.Code, disabled: v.Code === "finished" && record.status === 0 }))} value={text} onChange={(val) => onProjectStatusChange(val, record)} />
                );
            },
        },
        // {
        //     key: 'nextMilestone',
        //     dataIndex: 'nextMilestone',
        //     title: '下一个里程碑',
        //     width: 160,
        //     align: "center",
        //     render: (text, record) => {
        //         if (record.unsavedStatus || record.isCompleted || record.hasUnStandardMilestoneDate) {
        //             return <></>
        //         }
        //         return text
        //     }
        // },{
        //     key: 'status',
        //     dataIndex: 'status',
        //     title: '下一个里程碑状态',
        //     width: 160,
        //     align: "center",
        //     render: (text, record) => {
        //         if (record.unsavedStatus || record.hasUnStandardMilestoneDate) {
        //             return <></>
        //         }
        //         return (
        //             <Select options={statusOptions} value={record.status} disabled={record.status === 1} onChange={(val) => onRowStatusChange(val, record)} />
        //         );
        //     },
        // },
        {
            key: 'standardDate',
            dataIndex: 'standardDate',
            title: '下一个里程碑完成日期',
            width: 160,
            align: "center",
            render: (text, record) => {
                if (record.unsavedStatus || record.hasUnStandardMilestoneDate) {
                    return <></>
                }
                var isWarning = false
                let isNear = false
                if (record.standardDate) {
                    const curDate = new Date()
                    curDate.setHours(0,0,0,0)
                    const standardDate = new Date(record.standardDate)
                    standardDate.setHours(0, 0, 0, 0)
                    if (standardDate.getTime() <= curDate.getTime()) {
                        isWarning = true
                        
                    } else if (standardDate.getTime() < curDate.getTime() + 30 * 24 * 60 * 60 * 1000) {
                        isNear = true
                    }
                }
                if (!record.standardDate && record.status !== 1) {
                    isWarning = true
                }
                if (['finished', 'on_pause', 'discontinued'].includes(record.projectStatusCode)) {
                    isWarning = false
                    isNear = false
                }

                if (isWarning) {
                    return <div className="text-red-500">
                        <span className="mr-2">{text}</span>
                        <Tooltip placement="top" title="该里程碑已过计划时间，请修改状态或修改计划时间" color="red">
                            <QuestionCircleOutlined />
                        </Tooltip>
                    </div>
                }
                if (isNear) {
                    return <div className="text-yellow-500">
                        <span className="mr-2">{text}</span>
                    </div>
                }
                return text
            }
        },
        {
            key: '',
            title: '操作',
            width: 160,
            align: "center",
            render: (text, record) => {
                
                if (record.unsavedStatus) {
                    let isWarning = true
                    if (warnStatus.includes(record.projectStatusCode)) {
                        isWarning = false
                    }
                    return  <Button className={isWarning ? "text-red-500" : ""} onClick={() => onEdit(record)}>计划</Button>
                }
                return (
                    <Button className={record.hasUnStandardMilestoneDate ? "border-red-500 text-red-500" : ""} onClick={() => onEdit(record)}>整体调整</Button>
                );
            },
        }
    ]
    
    const onCompletedStatusChange = (val: boolean) => {
        setShowCompleted(val)
        setPage(1)
        onUpdateList({ page: 1, showCompleted: val })
    }
    const onAllStatusChange = (val: boolean) => {
        setHideInactive(val)
        setPage(1)
        onUpdateList({ page: 1, showAll: !val })
    }
    const minDate = useMemo(() => {
        const index = list?.findIndex(v => v.milestoneCode === currentEditItem?.nextMilestoneCode) || 0
        const minDate = getMinDateForMilestone(list || [], index, true);
        return minDate
    }, [list, currentEditItem, ])
    return (
        <div>
            <IthHelmet pageTitle="标准里程碑批量编辑" />
            <Card>
                <div className="flex justify-between items-center mb-2">
                    <div className="flex items-center gap-4">
                        <Input.Search
                            placeholder="请输入项目名称/编号"
                            allowClear
                            onSearch={onSearch}
                            onChange={(e) => {
                                if (!e.target.value) {
                                    onSearch("")
                                }
                            }}
                            style={{ width: 260 }}
                        />
                    </div>
                    <div className="flex items-center">
                        <Switch value={onlyShowPA} onChange={(val) => { setOnlyShowPA(val); onUpdateList({ onlyShowPa: val }) }} />
                        <div className="ml-1 mr-4 whitespace-nowrap">仅显示承担主创/设总角色的项目</div>
                        <Switch value={hideInactive} onChange={onAllStatusChange} />
                        <div className="ml-1 mr-4 whitespace-nowrap">仅显示有工时的项目</div>
                        <Switch value={showCompleted} onChange={onCompletedStatusChange} />
                        <div className="ml-1 whitespace-nowrap">显示已完成/已中止/已暂停项目</div>
                    </div>
                    
                </div>
                <div className="text-red-500 text-xs mb-2">* 如有项目需要分期/分地块，但又无法通过新立项解决的，需点击下方对应项目名称完成“设置地块”操作。</div>
                
                <Table
                    loading={loading}
                    tableLayout="auto"
                    rowKey={(record) => `${record.id}-${record.plot?.ID || ""}`}
                    rowClassName={(record, index) => {
                        if(record.id === currentEditMilestone?.id && record.plot?.ID === currentEditMilestone?.plot?.ID) return "is-row-selected"
                        if (record.id === currentEditItem?.id && record.plot?.ID === currentEditItem?.plot?.ID) return "is-row-selected"
                        return ""
                    }}
                    columns={tableColumns}
                    dataSource={renderList}
                    pagination={false}
                    className={`${styles.ppmTable}`}
                    locale={{
                        emptyText: (
                            <ThEmpty
                                description={(
                                    <div className="text-gray-400/50 mt-4">
                                        暂无数据
                                    </div>
                                )}
                            />
                        ),
                    }}
                />
            </Card>
            <Card>
                <Pagination current={page} onChange={(v) => onPageChange(v)} total={data?.total} pageSize={pageSize.current} onShowSizeChange={(cur, size) => {
                    pageSize.current = size
                }
                } />
            </Card>
            <Modal title="节点时间与状态设置" width="fit-content" maskClosable={false} destroyOnHidden style={{ minWidth: 750 }} open={editModalVisible} onCancel={onCloseEdit} footer={null}>
                <Spin spinning={listLoading}>   
                    <Edit values={list} isBatch={true} projectID={currentEditMilestone?.id} plotID={currentEditMilestone?.plot?.ID} onUpdate={onEditComplete} onCancel={onCloseEdit}></Edit>
                </Spin> 
            </Modal>
            <Modal title="请选择完成时间" width="fit-content" destroyOnHidden maskClosable={false} open={showCompletedDatePop} onCancel={onCancelEditStatus} footer={null}>
                <div>
                    <div className="text-xs text-red-500 mb-2">* 请注意已完成状态的里程碑保存后将不可编辑</div>
                    {currentEditItem?.showTips && (
                        <div className="text-gray-700 mb-2 max-w-[520px]">当前里程碑为该项目最后一个未完成的里程碑，状态设置为完成后，PLM项目空间中所有未完成任务的状态也将被自动修改为“已完成"，是否确认提交?</div>
                    )}
                    {currentEditItem?.nextMilestoneCode === k_circleNodeParentCode && (
                        <div className="flex justify-between items-center p-3 gap-4">
                            <span>外协交圈时间</span>
                            <DatePicker defaultValue={circleCompletionDate.current && dayjs(circleCompletionDate.current)}
                                showNow={false}
                                maxDate={dayjs(currentDate.current)}
                                onChange={(_, dateStr) => circleCompletionDate.current = dateStr as string}
                            />
                        </div>
                    )}
                    <div className="flex justify-between items-center p-3 gap-4">
                        <span>{currentEditItem?.nextMilestone || ""}</span>
                        <DatePicker defaultValue={completedDate.current && dayjs(completedDate.current)}
                            showNow={false}
                            disabledDate={(current) => {
                                if (minDate && current) {
                                    return current < minDate.startOf('day');
                                }
                                return false;
                            }}
                            maxDate={dayjs(currentDate.current)} 
                            onChange={(_, dateStr) => completedDate.current = dateStr as string}
                        />
                        
                    </div>
                    
                    <div className="flex justify-center mt-2">
                        <Button className="ml-4" onClick={onCancelEditStatus}>
                            取消
                        </Button>
                        <ConfigProvider
                            theme={{
                                token: {
                                    colorPrimary: "#161616",
                                }
                            }}>
                            <Button type="primary" className="ml-4" onClick={onSaveEditStatus}>
                                保存
                            </Button>
                        </ConfigProvider>
                    </div>
                </div>
            </Modal>
            <Modal title="" width="fit-content" destroyOnHidden maskClosable={false} open={showProjectStatusWarnPop} onCancel={onCancelEditStatus} footer={null}>
                <div className="pt-6">
                    <div className="text-xs text-red-500">* 请注意项目状态修改为“已完成”、“已中止”、“已暂停”的项目，下次进入页面将不会显示</div>
                    <div className="flex justify-between items-center pt-3">
                        <div className="flex">
                            <Button onClick={onCancelEditStatus}>
                                取消
                            </Button>
                            <ConfigProvider
                                theme={{
                                    token: {
                                        colorPrimary: "#161616",
                                    }
                                }}>
                                <Button type="primary" className="ml-4" onClick={onSaveProjectStatus}>
                                    保存
                                </Button>
                            </ConfigProvider>
                        </div>
                    </div>

                </div>
            </Modal>
        </div>
    )
}
export default Index